import React, { useState, useEffect, useRef } from 'react';
import { Building2, Store, ChefHat } from 'lucide-react';

const AUDIENCES = [
  {
    icon: Building2,
    title: 'Importers and distributors',
    body: 'Building or extending an Italian range without managing a dozen producer relationships yourself.',
  },
  {
    icon: Store,
    title: 'Retail chains and category managers',
    body: 'Needing consistent supply, private label capability and complete compliance documentation for every listing.',
  },
  {
    icon: ChefHat,
    title: 'Foodservice and specialty wholesalers',
    body: 'Sourcing authentic products with verified provenance and a story worth telling.',
  },
];

const WhoWeServeSection = () => {
  const [whoWeServeAnimated, setWhoWeServeAnimated] = useState(false);
  const whoWeServeRef = useRef(null);

  useEffect(() => {
    const whoWeServeObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !whoWeServeAnimated) {
          setWhoWeServeAnimated(true);
        }
      },
      { threshold: 0.2 }
    );

    if (whoWeServeRef.current) {
      whoWeServeObserver.observe(whoWeServeRef.current);
    }

    return () => {
      if (whoWeServeRef.current) {
        whoWeServeObserver.unobserve(whoWeServeRef.current);
      }
    };
  }, [whoWeServeAnimated]);

  return (
    <section
      id="who-we-serve"
      className="py-20 px-6 bg-gradient-to-br from-amber-50 via-stone-50 to-orange-50"
      ref={whoWeServeRef}
      data-testid="who-we-serve-section"
    >
      <div className={`container mx-auto max-w-7xl ${whoWeServeAnimated ? 'whoweserve-animate-active' : 'whoweserve-animate-ready'}`}>
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-blue-900 font-semibold tracking-wide uppercase text-sm whoweserve-label">WHO WE SERVE</p>
          <h2 className="md:text-5xl !font-bold !text-4xl mt-3 mb-6 text-stone-900">
            <span className="whoweserve-headline">Built for buyers like you</span>
          </h2>
          <p className="text-lg text-stone-600 whoweserve-subtext">Three kinds of business rely on us, each for a different reason.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {AUDIENCES.map((a, i) => {
            const Icon = a.icon;
            return (
              <div key={i} className="text-center space-y-4 group" data-testid={`who-we-serve-item-${i}`}>
                <div className="w-16 h-16 mx-auto bg-blue-100 rounded-2xl flex items-center justify-center group-hover:bg-blue-900 transition-colors duration-300">
                  <Icon className="text-blue-900 group-hover:text-white transition-colors duration-300" size={32} strokeWidth={2} />
                </div>
                <h3 className="text-xl font-bold text-stone-900">{a.title}</h3>
                <p className="text-stone-600 leading-relaxed">{a.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhoWeServeSection;
